import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '@/lib/supabase'
import type { Vehicle, LimoAnywhereVehicleType } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ImageUpload } from '@/components/vehicles/ImageUpload'
import { HighlightsEditor } from '@/components/vehicles/HighlightsEditor'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

import {
  vehicleSchema,
  VEHICLE_TYPES,
  VEHICLE_TYPE_LABELS,
  generateSlug,
  type VehicleFormData,
} from '@/schemas/vehicle'

interface QuoteNotifySettings {
  ghlEnabled: boolean
  hasNotifyContact: boolean
}

/** The account's custom-quote switch, so the form can say when a setting is inert. */
interface CustomQuoteSettings {
  enabled: boolean
}

interface VehicleFormProps {
  /** Existing vehicle when editing, null when creating a new one. */
  vehicle: Vehicle | null
  accountId: string
  basePath: string
  laVehicleTypes: LimoAnywhereVehicleType[]
  quoteNotifySettings: QuoteNotifySettings | null
  customQuoteSettings: CustomQuoteSettings | null
}

/**
 * Builds the initial form values once, from the loaded vehicle (or blank
 * defaults for a new vehicle). This component is mounted only after the vehicle
 * query has resolved and is keyed on the vehicle id by the parent, so
 * `useForm` initialises a single time with the correct data. That avoids the
 * controlled Radix Select desync that happens when the form is seeded with
 * placeholder defaults and later patched via the `values` prop.
 */
function buildDefaultValues(vehicle: Vehicle | null): VehicleFormData {
  if (!vehicle) {
    return {
      name: '',
      slug: '',
      description: '',
      vehicle_type: 'sedan',
      passenger_capacity: 4,
      luggage_capacity: null,
      year: null,
      make: '',
      model: '',
      color: '',
      image_url: '',
      is_active: true,
      is_quote_only: false,
      quote_distance_miles: null,
      highlights: [],
      limo_anywhere_vehicle_type_id: null,
    }
  }

  return {
    name: vehicle.name,
    slug: vehicle.slug,
    description: vehicle.description || '',
    vehicle_type: vehicle.vehicle_type as VehicleFormData['vehicle_type'],
    passenger_capacity: vehicle.passenger_capacity,
    luggage_capacity: vehicle.luggage_capacity,
    year: vehicle.year,
    make: vehicle.make || '',
    model: vehicle.model || '',
    color: vehicle.color || '',
    image_url: vehicle.image_url || '',
    is_active: vehicle.is_active,
    is_quote_only: vehicle.is_quote_only,
    quote_distance_miles: vehicle.quote_distance_miles ?? null,
    highlights: vehicle.highlights || [],
    limo_anywhere_vehicle_type_id: vehicle.limo_anywhere_vehicle_type_id || null,
  }
}

export function VehicleForm({
  vehicle,
  accountId,
  basePath,
  laVehicleTypes,
  quoteNotifySettings,
  customQuoteSettings,
}: VehicleFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const isEditing = !!vehicle

  const form = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: buildDefaultValues(vehicle),
  })

  // A quote-only vehicle is always quoted, so a mileage would never be reached —
  // the two controls are alternatives, not a pair. Either one is inert while the
  // account has custom quotes switched off, which is what the warning is for.
  const isQuoteOnly = form.watch('is_quote_only')
  const hasVehicleQuoteMileage = Number(form.watch('quote_distance_miles')) > 0

  const createMutation = useMutation({
    mutationFn: async (data: VehicleFormData) => {
      const { data: newVehicle, error } = await supabase
        .from('vehicles')
        .insert({
          ...data,
          account_id: accountId,
          image_url: data.image_url || null,
          description: data.description || null,
          luggage_capacity: data.luggage_capacity || null,
          year: data.year || null,
          make: data.make || null,
          model: data.model || null,
          color: data.color || null,
          highlights: data.highlights?.length ? data.highlights : null,
          limo_anywhere_vehicle_type_id: data.limo_anywhere_vehicle_type_id || null,
        })
        .select()
        .single()
      if (error) throw error
      return newVehicle
    },
    onSuccess: (newVehicle) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      toast({ title: 'Vehicle created successfully' })
      navigate(`${basePath}/vehicles/${newVehicle.id}`)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: VehicleFormData) => {
      if (!vehicle) throw new Error('Vehicle ID is required')
      const { error } = await supabase
        .from('vehicles')
        .update({
          ...data,
          image_url: data.image_url || null,
          description: data.description || null,
          luggage_capacity: data.luggage_capacity || null,
          year: data.year || null,
          make: data.make || null,
          model: data.model || null,
          color: data.color || null,
          highlights: data.highlights?.length ? data.highlights : null,
          limo_anywhere_vehicle_type_id: data.limo_anywhere_vehicle_type_id || null,
        })
        .eq('id', vehicle.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle?.id] })
      toast({ title: 'Vehicle updated successfully' })
      navigate(`${basePath}/vehicles/${vehicle?.id}`)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const onSubmit = (data: VehicleFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleNameBlur = () => {
    const name = form.getValues('name')
    const currentSlug = form.getValues('slug')
    if (name && !currentSlug) {
      form.setValue('slug', generateSlug(name))
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Vehicle Image</CardTitle>
            <CardDescription>Upload a photo of this vehicle</CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="max-w-md">
                      <ImageUpload
                        value={field.value}
                        onChange={field.onChange}
                        accountId={accountId}
                        disabled={isPending}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Enter the vehicle name and details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Lincoln Town Car"
                        {...field}
                        onBlur={() => {
                          field.onBlur()
                          handleNameBlur()
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug</FormLabel>
                    <FormControl>
                      <Input placeholder="lincoln-town-car" {...field} />
                    </FormControl>
                    <FormDescription>URL-friendly identifier</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="vehicle_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vehicle Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-[200px]">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VEHICLE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {VEHICLE_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the vehicle..."
                      className="min-h-[100px]"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vehicle Details</CardTitle>
            <CardDescription>
              Specify capacity and vehicle specifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="passenger_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passenger Capacity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="4"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : ''
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="luggage_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Luggage Capacity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="3"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>Number of bags</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1900}
                        max={new Date().getFullYear() + 1}
                        placeholder="2023"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Lincoln"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Continental"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Black"
                      className="w-full sm:w-[200px]"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Booking Highlights</CardTitle>
            <CardDescription>
              Add bullet points that appear next to this vehicle during booking
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="highlights"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <HighlightsEditor
                      value={field.value || []}
                      onChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    Examples: "Free cancellation 24 hours before pickup",
                    "Complimentary bottled water"
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Limo Anywhere Integration - only show if vehicle types are synced */}
        {laVehicleTypes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Limo Anywhere Integration</CardTitle>
              <CardDescription>
                Map this vehicle to a Limo Anywhere vehicle type for Zapier
                integration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="limo_anywhere_vehicle_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vehicle Type</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === 'none' ? null : value)
                      }
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full sm:w-[300px]">
                          <SelectValue placeholder="Select a vehicle type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None (not mapped)</SelectItem>
                        {laVehicleTypes.map((vt) => (
                          <SelectItem key={vt.id} value={vt.id}>
                            {vt.name} {vt.code ? `(${vt.code})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The Limo Anywhere vehicle type code will be sent to Zapier
                      when this vehicle is booked
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Visibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      This vehicle will be visible on your website
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_quote_only"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Quote-only vehicle</FormLabel>
                    <FormDescription>
                      Customers see a &quot;Get Quote&quot; button instead of a
                      price. You set the price for each request from a link sent
                      to your phone and email.
                    </FormDescription>
                    {field.value &&
                      quoteNotifySettings &&
                      (!quoteNotifySettings.ghlEnabled ||
                        !quoteNotifySettings.hasNotifyContact) && (
                        <p className="text-sm text-amber-600">
                          {!quoteNotifySettings.ghlEnabled
                            ? 'GoHighLevel is not enabled, so you will not receive instant text/email alerts for quote requests. '
                            : 'No notification phone or email is configured in Settings > Integrations, so you will not receive instant alerts. '}
                          Requests will still appear in your Quotes dashboard.
                        </p>
                      )}
                  </div>
                </FormItem>
              )}
            />
            {!isQuoteOnly && (
              <FormField
                control={form.control}
                name="quote_distance_miles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote trips longer than</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={5000}
                          placeholder="No limit"
                          className="w-36"
                          name={field.name}
                          ref={field.ref}
                          onBlur={field.onBlur}
                          value={field.value ?? ''}
                          onChange={(event) =>
                            field.onChange(
                              event.target.value === '' ? null : event.target.value
                            )
                          }
                        />
                        <span className="text-muted-foreground">miles</span>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Point-to-point trips over this distance show &quot;Get Quote&quot; instead
                      of a price. Leave blank to always show a calculated price. Routes with a
                      configured flat rate keep their flat price at any distance, and hourly
                      charters are never affected.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {customQuoteSettings && !customQuoteSettings.enabled && (isQuoteOnly || hasVehicleQuoteMileage) && (
              <p className="text-sm text-amber-600">
                Custom quotes are switched off for your account, so this vehicle still shows a
                calculated price. Turn on Custom Quotes (Get Quote) under Settings &gt; Booking
                to use it.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Vehicle'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`${basePath}/vehicles`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}
